import React, { createContext, useContext, useEffect, useState } from 'react';
import { IBotProperties, IProvider } from '../../store/providers/providerTypes';

interface IProps {
  children: React.ReactNode;
  provider: IProvider;
  onChangeChatStatus: (status: boolean) => void;
}

export interface IHospitalInitialInformation {
  name: string;
  logo: string;
  rootDocumentId: number;
  showCitation: boolean;
  botProperties: IBotProperties;
}

export interface ICategorisedSections {
  name: string;
  id: string;
  dadId: string;
}

interface IContextValues {
  hospitalInitialInformation: {
    showCitation: boolean;
    logo: string;
    name: string;
    chatHistoryItemId?: number;
    chatHistoryItemViewToken?: string;
    threadId?: string;
    botProperties: IBotProperties;
  };
  categorisedSections: Array<ICategorisedSections>;
  instruction: string;
  questionButtonList: Array<string>;
  onChangeChatStatus: (status: boolean) => void;
}

const cntx = createContext<IContextValues | null>(null);

const HopitalContextProvider: React.FC<IProps> = ({
  children,
  provider,
  onChangeChatStatus,
}) => {
  const [categorisedSections] = useState<ICategorisedSections[]>([]);
  const [instruction, setInstruction] = useState<string>('');

  function getInitialState() {
    return () => ({
      name: provider.name,
      logo: provider.documents.find(
        (item: any) =>
          item?.documentType?.name === 'profile_picture' ||
          item?.document?.documentType === 'profile_picture'
      )?.document.url,
      rootDocumentId: provider.rootDocument.id,
      chatHistoryItemId: provider.botProperties?.chatHistoryItemId,
      chatHistoryItemViewToken:
        provider.botProperties?.chatHistoryItemViewToken,
      botProperties: provider.botProperties,
      showCitation: provider.botProperties?.showCitation || false,
      threadId: provider.id.toString(),
    });
  }

  const [hospitalInitialInformation, setHospitalInitialInformation] =
    useState<IHospitalInitialInformation>(getInitialState());

  useEffect(() => {
    setHospitalInitialInformation(getInitialState());
  }, [provider]);

  const [questionButtonList, setQuestionButtonList] = useState<Array<string>>(
    []
  );

  useEffect(() => {
    if (provider?.botProperties?.assistantId) {
      setInstruction(provider.botProperties.systemInstructions ?? '');
    }

    if (provider?.botProperties?.defaultPrompts) {
      setQuestionButtonList([...provider.botProperties.defaultPrompts]);
    }
  }, [hospitalInitialInformation.rootDocumentId, location.pathname]);

  return (
    <cntx.Provider
      value={{
        hospitalInitialInformation,
        categorisedSections,
        instruction,
        questionButtonList,
        onChangeChatStatus,
      }}
    >
      {children}
    </cntx.Provider>
  );
};

export const useHospitalContext = () => {
  return useContext(cntx);
};

export default HopitalContextProvider;
